import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AccordionSummary, Box, Chip, Stack, Typography } from "@mui/material";
import { IGroupedV2Clause } from "./types";

interface V2ClauseAccordionSummaryProps {
    clauseGroup: IGroupedV2Clause;
    panelId: string;
    isExpanded?: boolean;
}

export const V2ClauseAccordionSummary = ({
    clauseGroup,
    panelId,
}: V2ClauseAccordionSummaryProps) => {
    const isFound = clauseGroup.scopeClauses.some((sc) => sc.found);
    const highlightColor = clauseGroup.highlightColor || "#3b82f6";

    return (
        <AccordionSummary
            expandIcon={
                <ExpandMoreIcon
                    sx={{
                        fontSize: 18,
                        color: "#94a3b8",
                        transition: "color 0.15s ease",
                    }}
                />
            }
            sx={{
                minHeight: 72,
                pl: "15px", // 16px - 1px border = align with topic title
                pr: 2,
                "&:hover": {
                    bgcolor: "rgba(34, 56, 118, 0.02)",
                    "& .MuiSvgIcon-root": {
                        color: "#64748b",
                    },
                },
                "&.Mui-expanded": {
                    minHeight: 72,
                },
                "& .MuiAccordionSummary-content": {
                    my: 1.5,
                    "&.Mui-expanded": {
                        my: 1.5,
                    },
                },
            }}
            id={`${panelId}-header`}
            aria-controls={`${panelId}-content`}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%", pr: "3px" }}
            >
                {/* Left side: color bar + content */}
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1.5}
                    flex={1}
                    minWidth={0}
                >
                    {/* Color bar */}
                    <Box
                        sx={{
                            width: 4,
                            minWidth: 4,
                            alignSelf: "stretch",
                            bgcolor: highlightColor,
                            borderRadius: 1,
                            my: 0.5,
                        }}
                    />

                    {/* Content */}
                    <Stack gap={1} flex={1} minWidth={0}>
                        {/* Title */}
                        <Typography
                            sx={{
                                fontWeight: 500,
                                fontSize: "0.9375rem",
                                lineHeight: 1.4,
                                color: "#1e293b",
                            }}
                        >
                            {clauseGroup.clause.title}
                        </Typography>

                        {/* Tags row */}
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                            <Chip
                                label={
                                    clauseGroup.clause.policy_type.short_name
                                }
                                variant="outlined"
                                size="small"
                                sx={{
                                    fontSize: "0.75rem",
                                    height: "24px",
                                    fontWeight: 600,
                                    borderColor: "#475569",
                                    color: "#334155",
                                }}
                            />
                            {(clauseGroup.sectionNames ?? []).map(
                                (sectionName, index) => (
                                    <Chip
                                        key={`${sectionName}-${index}`}
                                        label={sectionName}
                                        size="small"
                                        sx={{
                                            fontSize: "0.75rem",
                                            height: "24px",
                                            bgcolor: "#f1f5f9",
                                            color: "#475569",
                                        }}
                                    />
                                )
                            )}
                        </Stack>
                    </Stack>
                </Stack>

                {/* Right side: status indicator (same size as count badge) */}
                {isFound && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 24,
                            height: 24,
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            sx={{
                                width: 14,
                                height: 14,
                                borderRadius: "50%",
                                bgcolor: `${highlightColor}33`,
                                border: `2px solid ${highlightColor}`,
                            }}
                        />
                    </Box>
                )}
            </Stack>
        </AccordionSummary>
    );
};
